import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Chip,
  IconButton,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Fingerprint as FingerprintIcon,
} from '@mui/icons-material';

const FormularioCard = React.memo(({ 
  form, 
  isMobile, 
  isSmallMobile, 
  openEdit, 
  abrirModalUsuarios, 
  handleDelete, 
  handleToggleStatus,
  formatDate,
  formatDateTime
}) => {
  const getStatusColor = (estado) => {
    const colors = {
      activo: 'success', 
      inactivo: 'default', 
      pendiente: 'warning',
      expirado: 'error'
    };
    return colors[estado] || 'default';
  };

  const determinarEstado = (formulario) => {
    const ahora = new Date();
    const fechaInicio = formulario.fecha_inicio ? new Date(formulario.fecha_inicio) : null;
    const fechaFin = formulario.fecha_fin ? new Date(formulario.fecha_fin) : null;
    
    if (formulario.estado === 'inactivo') return 'inactivo';
    if (!fechaInicio && !fechaFin) return formulario.estado || 'activo';
    if (fechaInicio && ahora < fechaInicio) return 'pendiente';
    if (fechaFin && ahora > fechaFin) return 'expirado';
    if (formulario.estado === 'activo') return 'activo';
    return formulario.estado || 'activo';
  };

  const getFechaInfo = (formulario) => {
    const ahora = new Date();
    const fechaInicio = formulario.fecha_inicio ? new Date(formulario.fecha_inicio) : null;
    const fechaFin = formulario.fecha_fin ? new Date(formulario.fecha_fin) : null;
    
    if (!fechaInicio && !fechaFin) return null;
    if (fechaInicio && ahora < fechaInicio) {
      const diffTime = fechaInicio - ahora;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `Inicia en ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    }
    if (fechaFin && ahora > fechaFin) {
      const diffTime = ahora - fechaFin;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `Expirado hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    }
    if (fechaFin && ahora <= fechaFin) {
      const diffTime = fechaFin - ahora;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `Vence en ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    }
    return null;
  };

  const dynamicStatus = determinarEstado(form);
  const fechaInfo = getFechaInfo(form);

  // Mostrar fecha de creación con hora (usando la función del padre)
  const fechaCreacion = form.fecha_creacion 
    ? formatDateTime(form.fecha_creacion) 
    : form.fecha_actualizacion 
      ? formatDateTime(form.fecha_actualizacion)
      : 'No disponible';


  return (
    <Card elevation={3} sx={{ 
      width: '100%',
      opacity: dynamicStatus === 'expirado' ? 0.8 : 1,
      borderLeft: dynamicStatus === 'expirado' ? '4px solid #f44336' : 
                 dynamicStatus === 'pendiente' ? '4px solid #ff9800' :
                 dynamicStatus === 'activo' ? '4px solid #4caf50' : '4px solid #9e9e9e'
    }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Box>
            <Typography variant="h6" component="h2" sx={{ fontSize: '1.1rem', lineHeight: 1.2, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
              {form.titulo}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.25}>
              <Box display="flex" alignItems="center">
                <CalendarIcon fontSize="small" sx={{ fontSize: 14, mr: 0.25, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary">
                  Creado: {fechaCreacion}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Chip 
              label={dynamicStatus.toUpperCase()} 
              color={getStatusColor(dynamicStatus)} 
              size="small"
              sx={{ fontWeight: 'bold', fontSize: '0.65rem', height: 24 }}
            />
            <IconButton color="primary" onClick={() => openEdit(form)} size="small" sx={{ p: 0.5 }}>
              <EditIcon fontSize="small" sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton color="secondary" onClick={() => abrirModalUsuarios(form.id_formulario_general)} size="small" sx={{ p: 0.5 }}>
              <PersonAddIcon fontSize="small" sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton color="error" onClick={() => handleDelete(form.id_formulario_general)} size="small" sx={{ p: 0.5 }}>
              <DeleteIcon fontSize="small" sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {fechaInfo && (
          <Box display="flex" alignItems="center" mt={0.25} mb={0.5}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="textSecondary">
              {fechaInfo}
            </Typography>
          </Box>
        )}

        {form.descripcion && (
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem', mt: 0.5, mb: 0.75, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
            {form.descripcion}
          </Typography>
        )}

        <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 0.75, mb: 0.75 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <strong>Inicio:</strong> {form.fecha_inicio ? form.fecha_inicio.replace(/-/g, '/') : 'No disponible'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <strong>Fin:</strong> {form.fecha_fin ? form.fecha_fin.replace(/-/g, '/') : 'No disponible'}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.estado === 'activo'}
                onChange={() => handleToggleStatus(form)}
                size="small"
                disabled={dynamicStatus === 'expirado' || dynamicStatus === 'pendiente'}
                sx={{ 
                  '& .MuiSwitch-switchBase': { 
                    padding: '6px',
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 1.5,
                  }
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {dynamicStatus === 'expirado' ? 'Expirado' :
                 dynamicStatus === 'pendiente' ? 'Pendiente' :
                 form.estado === 'activo' ? 'Activo' : 'Inactivo'}
              </Typography>
            }
            sx={{ m: 0 }}
          />
          <Box />
        </Box>
      </CardContent>
    </Card>
  );
});

export default FormularioCard;